import React, { useState, useEffect } from "react";
import './Register.scss';
import '../../Login/Mob/Login.scss';
import axios from 'axios';
import { useNavigate, Link } from "react-router-dom";
import { useNoProfile } from "../../../hooks/useProfileData";
import { GuestForm } from "../../GuestForm";
import { GoogleSignInButton } from "../../GoogleSignInButton";
import { setPendingCouponCode } from "../../../utils/couponsApi";
import { setPreferredNotifyChannel } from "../../../utils/notifyChannel";
import {
  buildRegisterPayload,
  getRegistrationValidity,
  navigateAfterRegistration,
  validateRegistrationForm,
} from "../registerHelpers";
import { noAutofillProps } from "../../noAutofillProps";

export function MobRegister() {
  useNoProfile();

  useEffect(() => {
    localStorage.removeItem('resendTime');
  }, []);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    pincode: ''
  });

  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [showGuestForm, setShowGuestForm] = useState(false);
  const navigate = useNavigate();

  const { hasValidPhone, hasValidEmail } = getRegistrationValidity(formData);
  const showVerifyOptions = hasValidPhone || hasValidEmail;

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === 'phone') {
      const phone = value.replace(/\D/g, '').slice(0, 10);
      setFormData((prev) => ({
        ...prev,
        phone,
        ...(phone ? { email: '' } : {}),
      }));
      return;
    }
    if (name === 'email') {
      setFormData((prev) => ({
        ...prev,
        email: value,
        ...(value.trim() ? { phone: '' } : {}),
      }));
      return;
    }
    if (name === 'pincode') {
      setFormData((prev) => ({
        ...prev,
        pincode: value.replace(/\D/g, '').slice(0, 6),
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const registerWithVerification = async (channel) => {
    const validationError = validateRegistrationForm(formData);
    if (validationError) {
      setResponseMessage(validationError);
      return;
    }

    if (channel === 'email' && !hasValidEmail) {
      setResponseMessage('Please enter a valid email address.');
      return;
    }

    if ((channel === 'sms' || channel === 'whatsapp') && !hasValidPhone) {
      setResponseMessage('Please enter a valid 10-digit mobile number.');
      return;
    }

    setLoading(true);
    setResponseMessage('');

    try {
      const response = await axios.post(
        '/users/register',
        buildRegisterPayload(formData, channel)
      );

      if (response.status === 200) {
        setPendingCouponCode('WELCOME20');
        if (channel === 'whatsapp' || channel === 'sms' || channel === 'email') {
          setPreferredNotifyChannel(channel);
        }
        navigateAfterRegistration(navigate, {
          channel,
          formData,
          responseData: response.data?.data || {},
        });
      }
    } catch (error) {
      console.error('Registration error:', error);
      const status = error.response?.status;
      const msg = error.response?.data?.msg || error.response?.data?.message;
      if (status === 409) {
        setResponseMessage(
          `${msg || 'This account already exists.'} Try logging in instead.`
        );
      } else {
        setResponseMessage(msg || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setResponseMessage('Please choose Phone OTP or Email code to verify and create your account.');
  };

  return (
    <div className={`mob-authPage mob-register-page${loading ? ' auth-busy' : ''}`} aria-busy={loading}>
      <div className="auth-container mob-register-page__container">
        <div
          className="LazyLoad is-visible mob-register-page__hero"
        >
          <picture className="img-responsive">
            <source
              srcSet="//assets.myntassets.com/f_webp,dpr_1.5,q_60,w_400,c_limit,fl_progressive/assets/images/2023/10/29/9610da61-a1a4-4215-b1fa-f612242d10e61698602536819-Flat_200--1-.jpg"
              type="image/webp"
            />
            <img
              src="//assets.myntassets.com/dpr_1.5,q_60,w_400,c_limit,fl_progressive/assets/images/2023/10/29/9610da61-a1a4-4215-b1fa-f612242d10e61698602536819-Flat_200--1-.jpg"
              className="img-responsive preLoad loaded"
              alt="Register Background"
              title=""
            />
          </picture>
        </div>
        <div className="signInContainer mob-register-page__content">
          <div className="welcome-header mob-register-page__header">
            Create Account
            <span className="welcome-header-small mob-register-page__header-small">Join us today!</span>
          </div>
          <div className="mobileInputContainer mob-register-page__input-wrap">
            <form onSubmit={handleSubmit} autoComplete="off">
              <div className="form-group mob-register-page__form-group">
                <input
                  type="text"
                  name="firstName"
                  className="form-control mobileNumberInput mob-register-page__control"
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  disabled={loading}
                  required
                  {...noAutofillProps()}
                />
                <span className="placeholderAlternative mobileNumber mob-register-page__placeholder">
                  First Name <span style={{ color: '#ff5722' }}>*</span>
                </span>
                <i className="bar"></i>
              </div>

              <div className="form-group mob-register-page__form-group">
                <input
                  type="text"
                  name="lastName"
                  className="form-control mobileNumberInput mob-register-page__control"
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  disabled={loading}
                  required
                  {...noAutofillProps()}
                />
                <span className="placeholderAlternative mobileNumber mob-register-page__placeholder">
                  Last Name <span style={{ color: '#ff5722' }}>*</span>
                </span>
                <i className="bar"></i>
              </div>

              <div className="auth-identifier-group">
                <div className="form-group mob-register-page__form-group">
                  <input
                    type="tel"
                    name="phone"
                    className="form-control mobileNumberInput mob-register-page__control"
                    placeholder="Mobile Number"
                    maxLength="10"
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={loading}
                    inputMode="numeric"
                    {...noAutofillProps()}
                  />
                  <span className="placeholderAlternative mobileNumber mob-register-page__placeholder">
                    +91
                    <span className="mob-register-page__divider-pipe">|</span>
                    Mobile Number <span className="field-optional-label">(or email below)</span>
                  </span>
                  <i className="bar"></i>
                </div>

                <div className="auth-field-or mob-register-page__or">OR</div>

                <div className="form-group mob-register-page__form-group">
                  <input
                    type="email"
                    name="email"
                    className="form-control mobileNumberInput mob-register-page__control"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={loading}
                    {...noAutofillProps()}
                  />
                  <span className="placeholderAlternative mobileNumber mob-register-page__placeholder">
                    Email Address <span className="field-optional-label">(or mobile above)</span>
                  </span>
                  <i className="bar"></i>
                </div>
              </div>

              <div className="form-group mob-register-page__form-group">
                <input
                  type="tel"
                  name="pincode"
                  className="form-control mobileNumberInput mob-register-page__control"
                  placeholder="Pincode"
                  maxLength="6"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  disabled={loading}
                  inputMode="numeric"
                  {...noAutofillProps()}
                />
                <span className="placeholderAlternative mobileNumber mob-register-page__placeholder">
                  Pincode <span className="field-optional-label">(optional)</span>
                </span>
                <i className="bar"></i>
              </div>

              {showVerifyOptions ? (
                <div className="login-verify-group">
                  <span className="login-verify-group__label">Verify using</span>
                  <div className="login-otp-options">
                    <div className="login-otp-options__links">
                      <button
                        type="button"
                        className={`login-otp-options__btn ${hasValidPhone ? '' : 'login-otp-options__btn--disabled'}`}
                        disabled={!hasValidPhone || loading}
                        onClick={() => registerWithVerification('sms')}
                      >
                        <i className="bi bi-phone"></i>
                        Phone OTP
                      </button>
                      {/* WhatsApp OTP is paused. Registration currently uses phone SMS and email only.
                      <span className="login-otp-options__divider">|</span>
                      <button
                        type="button"
                        className={`login-otp-options__btn ${hasValidPhone ? '' : 'login-otp-options__btn--disabled'}`}
                        disabled={!hasValidPhone || loading}
                        onClick={() => registerWithVerification('whatsapp')}
                      >
                        <i className="bi bi-whatsapp"></i>
                        WhatsApp OTP
                      </button>
                      */}
                      <span className="login-otp-options__divider">|</span>
                      <button
                        type="button"
                        className={`login-otp-options__btn ${hasValidEmail ? '' : 'login-otp-options__btn--disabled'}`}
                        disabled={!hasValidEmail || loading}
                        onClick={() => registerWithVerification('email')}
                      >
                        <i className="bi bi-envelope"></i>
                        Email code
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="mob-register-page__hint">
                  Enter mobile or email above, then choose a verification method.
                </p>
              )}

              {responseMessage && (
                <div className="responseMessage mob-register-page__response">
                  <i className="bi bi-exclamation-circle-fill"></i>
                  <span>{responseMessage}</span>
                </div>
              )}

              <div className="midLinks mob-register-page__links">
                By continuing, I agree to the{' '}
                <a href="/terms">Terms of Use</a> &amp;{' '}
                <a href="/privacy">Privacy Policy</a>
              </div>

              <button
                type="submit"
                className="submitBottomOption mob-register-page__submit"
                disabled={loading || !showVerifyOptions}
              >
                {loading ? 'Creating Account...' : 'CREATE ACCOUNT'}
              </button>

              <div className="mob-auth-divider mob-auth-divider--pill mob-register-page__divider">
                <span className="mob-auth-divider__text mob-register-page__divider-text">OR</span>
              </div>

              <div className="mob-oauth-buttons mob-register-page__oauth-buttons">
                <GoogleSignInButton
                  variant="mob"
                  disabled={loading}
                  onError={(msg) => setResponseMessage(msg)}
                />
                <button type="button" className="mob-oauth-btn mob-oauth-btn--facebook mob-register-page__oauth-btn">
                  <i className="bi bi-facebook"></i>
                  <span>Continue with Facebook</span>
                </button>
              </div>

              <div className="mob-auth-divider mob-auth-divider--pill mob-register-page__divider">
                <span className="mob-auth-divider__text mob-register-page__divider-text">OR</span>
              </div>

              <button type="button" className="mob-guest-btn mob-register-page__guest-btn" onClick={() => setShowGuestForm(true)}>
                LOGIN AS GUEST
              </button>
            </form>
            {showGuestForm && (
              <GuestForm onClose={() => setShowGuestForm(false)} onSuccess={() => setShowGuestForm(false)} />
            )}
          </div>

          <div className="get-help mob-register-page__help">
            Already have an account? <Link to="/auth/login">Login</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
